import {useSelector} from 'react-redux';

import {isNil, map} from 'ramda';

import {
  useGetEmployeeMechanicsByBranchQuery,
  useGetTireAspectRatiosQuery,
  useGetTireCategoriesQuery,
  useGetTireConditionsQuery,
  useGetTireLoadIndexesQuery,
  useGetTireLocationsQuery,
  useGetTireManufacturerQuery,
  useGetTireOptionsQuery,
  useGetTireRimsQuery,
  useGetTireSpeedRatiosQuery,
  useGetTireWidthsQuery,
} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';

import {Nullish} from 'shared/web';

export function useTireSetOptions(homeBranchId: string | Nullish) {
  const activeBranchId = useSelector(selectActiveBranchId);

  const {data: seasonOptions, isLoading: isSeasonOptionsLoading} = useGetTireOptionsQuery({
    category: 'TIRTIRESEASON',
  });
  const {data: locations, isLoading: isLocationOptionsLoading} = useGetTireLocationsQuery();
  const {data: rimTypeOptions, isLoading: isRimTypeOptionsLoading} = useGetTireOptionsQuery({
    category: 'TIRRIMTYPE',
  });
  const {data: manufacturers, isLoading: isManufacturerOptionsLoading} =
    useGetTireManufacturerQuery(undefined);
  const {data: mechanics, isLoading: isMechanicOptionsLoading} =
    useGetEmployeeMechanicsByBranchQuery({branchId: activeBranchId});
  const {data: tireWidths, isLoading: isTireWidthOptionsLoading} = useGetTireWidthsQuery();
  const {data: aspectRatios, isLoading: isAspectRatioOptionsLoading} =
    useGetTireAspectRatiosQuery();
  const {data: loadIndexes, isLoading: isLoadIndexOptionsLoading} = useGetTireLoadIndexesQuery();
  const {data: speedRatios, isLoading: isSpeedRatioOptionsLoading} = useGetTireSpeedRatiosQuery();
  const {data: tireRims, isLoading: isRimOptionsLoading} = useGetTireRimsQuery();
  const {data: tireCategories, isLoading: isTireCategoryOptionsLoading} = useGetTireCategoriesQuery(
    {branchId: homeBranchId}
  );
  const {data: tireConditions, isLoading: isTireConditionOptionsLoading} =
    useGetTireConditionsQuery();

  const manufacturerOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.id ?? ''}),
    manufacturers?.filter((item) => item?.isActive) ?? []
  );

  const tireWidthOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireWidthId ?? ''}),
    tireWidths?.filter((item) => item?.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const aspectRatioOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireAspectRatioId ?? ''}),
    aspectRatios?.filter((item) => item?.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const rimOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireRimId ?? ''}),
    tireRims?.filter((item) => item?.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const loadIndexOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireLoadIndexId ?? ''}),
    loadIndexes?.filter((item) => item?.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const speedRatioOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireSpeedRatioId ?? ''}),
    speedRatios?.filter((item) => item?.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));
  const categoryOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireCategoryId ?? ''}),
    tireCategories?.filter(
      (item) =>
        item?.isActive && (item?.branchIds?.includes(homeBranchId ?? '') || isNil(item?.branchIds))
    ) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const tireConditionOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.tireConditionId ?? ''}),
    tireConditions?.filter((item) => item?.isActive) ?? []
  );

  const defaultTireCondition = tireConditions?.find((item) => item?.isDefault);

  const defaultCategory = tireCategories?.find((item) => item?.isDefault);

  const getManufacturerOptionsWithSelectedValue = (selectedManufacturer: string | Nullish) =>
    map(
      (item) => ({label: item?.name ?? '', value: item?.id ?? ''}),
      manufacturers?.filter((item) => item?.isActive || item?.id === selectedManufacturer) ?? []
    );

  const locationOptions = map(
    (item) => ({label: item?.name ?? '', value: item?.id ?? ''}),
    locations?.filter((item) => item?.isActive) ?? []
  );

  const getLocationOptionsWithSelectedValue = (selectedLocation: string | Nullish) =>
    map(
      (item) => ({label: item?.name ?? '', value: item?.id ?? ''}),
      locations?.filter((item) => item?.isActive || item?.id === selectedLocation) ?? []
    );

  const mechanicOptions = map(
    (item) => ({label: item.label, value: item.value}),
    mechanics?.filter((item) => item.isActive) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const defaultManufacturer = manufacturers?.find((manufacturer) => manufacturer?.isDefault);

  const isLoading =
    isSeasonOptionsLoading ||
    isLocationOptionsLoading ||
    isRimOptionsLoading ||
    isManufacturerOptionsLoading ||
    isMechanicOptionsLoading ||
    isTireWidthOptionsLoading ||
    isAspectRatioOptionsLoading ||
    isLoadIndexOptionsLoading ||
    isSpeedRatioOptionsLoading ||
    isRimTypeOptionsLoading ||
    isTireCategoryOptionsLoading ||
    isTireConditionOptionsLoading;

  return {
    seasonOptions,
    locationOptions,
    rimOptions,
    manufacturerOptions,
    mechanicOptions,
    defaultManufacturer,
    defaultTireCondition,
    tireWidthOptions,
    aspectRatioOptions,
    loadIndexOptions,
    speedRatioOptions,
    rimTypeOptions,
    categoryOptions,
    defaultCategory,
    isLoading,
    tireConditionOptions,
    getManufacturerOptionsWithSelectedValue,
    getLocationOptionsWithSelectedValue,
  };
}
